import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading, Empty, Field } from "@/components/site/ui";
import StatusBadge from "@/components/StatusBadge";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none";
const CATS = ["Extra towels", "Food request", "Drink request", "Ice", "Pool temperature", "Music request", "Lighting theme", "Late checkout", "Celebration setup", "Maintenance", "General request"];

export default function PortalConcierge() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ category: CATS[0], priority: "normal", notes: "", desired_time: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/portal/concierge").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.notes.trim()) { toast.error("Add a few details for your request."); return; }
    setSending(true);
    try { await api.post("/portal/concierge", f); toast.success("Request sent to concierge."); setF({ category: CATS[0], priority: "normal", notes: "", desired_time: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <h2 className="font-serif text-2xl mb-4">New Request</h2>
        <form onSubmit={submit} className="glass rounded-lg p-6 space-y-4">
          <Field label="Category"><select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="concierge-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Priority"><select className={inputCls} value={f.priority} onChange={(e) => set("priority", e.target.value)}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></Field>
          <Field label="Details" required><textarea rows={3} className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="concierge-notes" /></Field>
          <Field label="Desired time"><input className={inputCls} value={f.desired_time} onChange={(e) => set("desired_time", e.target.value)} placeholder="e.g. before 6pm" /></Field>
          <button disabled={sending} className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="concierge-submit">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Send Request
          </button>
        </form>
      </div>
      <div>
        <h2 className="font-serif text-2xl mb-4">Your Requests</h2>
        {items === null ? <Loading /> : items.length === 0 ? <Empty title="No requests yet" /> : (
          <div className="space-y-3">
            {items.map((c) => (
              <div key={c.id} className="glass rounded-lg p-5" data-testid={`concierge-item-${c.id}`}>
                <div className="flex justify-between items-start">
                  <div><p className="font-medium">{c.category}</p><p className="text-xs text-muted-foreground mt-1">{c.notes}</p></div>
                  <StatusBadge status={c.status} />
                </div>
                {c.messages?.filter((m) => m.from === "host").map((m) => (
                  <p key={m.id} className="text-xs text-pool mt-3 border-l-2 border-pool/40 pl-3">Host: {m.text}</p>
                ))}
                <p className="text-[10px] text-muted-foreground mt-2">{fmtDate(c.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
